import React, { useState, useEffect, useContext } from "react";
import { Bar, Pie } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import dayjs from "dayjs";
import { FaCar, FaUsers, FaDollarSign, FaTools } from "react-icons/fa";
import axios from "axios";
import GlobalContext from "../connections/GlobalContext";
import engineGif from "../image/engine.gif";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const Dashboard = ({ theme }) => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [currentTime, setCurrentTime] = useState(dayjs().format("HH:mm:ss"));
  const [isMobile, setIsMobile] = useState(false);
  const { globalValue } = useContext(GlobalContext);
  const [loading, setLoading] = useState(false);

  const [dashData, setDashData] = useState({});
  const [sparesData, setSparesData] = useState({
    Taken: 0,
    Returned: 0,
    Replaced: 0
  });
  const [earningsPercentages, setEarningsPercentages] = useState([]);

  useEffect(() => {
    const updateScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    updateScreenSize();
    window.addEventListener("resize", updateScreenSize);
    return () => window.removeEventListener("resize", updateScreenSize);
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true); 
    try {
        const response = await axios.post(`${globalValue}/dashboard`, null, {
            headers: {
                'x-access-token': sessionStorage.getItem('token'),
            },
        });

        if (response.status !== 200) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = response.data;

        setDashData(data);
        setSparesData({
            Taken: data.spares_taken,
            Returned: data.spares_returned,
            Replaced: data.spares_replaced,
        });
        setEarningsPercentages(data.earnings_percentages);
    } catch (error) {
        console.error("Error fetching dashboard data:", error);
    } finally {
        setLoading(false); 
    }
};


  useEffect(() => {
    fetchDashboardData();
    const timer = setInterval(() => {
      setCurrentTime(dayjs().format("HH:mm:ss"));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const cards = [
    { id: 1, title: "Total Cars", value: dashData.total_estimates, detail: "Details about total cars", icon: <FaCar size={40} />, progress: 80 },
    { id: 2, title: "Total Staffs", value: dashData.total_staffs, detail: "Details about total staffs", icon: <FaUsers size={40} />, progress: 65 },
    { id: 7, title: "Total Spares", value: dashData.total_spares, detail: "Details about spares", icon: <FaTools size={40} />, progress: 60 },
    { id: 8, title: "Low Quantity Spares", value: dashData.low_quantity_spares, detail: "Low quantity spares", icon: <FaTools size={40} />, progress: 30 },
    { id: 3, title: "Total Earnings", value: "Rs." + dashData.total_earnings, detail: "Earnings summary", icon: <FaDollarSign size={40} />, progress: 90 },
    { id: 4, title: "Today Earnings", value: "Rs." + dashData.today_earnings, detail: "Earnings for today", icon: <FaDollarSign size={40} />, progress: 50 },
    { id: 5, title: "This Week Earnings", value: "Rs." + dashData.week_earnings, detail: "Weekly earnings", icon: <FaDollarSign size={40} />, progress: 70 },
    { id: 6, title: "This Month Earnings", value: "Rs." + dashData.month_earnings, detail: "Monthly earnings", icon: <FaDollarSign size={40} />, progress: 85 },
  ];

  const toggleCard = (id) => {
    if (!isMobile) {
      setExpandedCard(expandedCard === id ? null : id);
    }
  };

  const months = earningsPercentages.map(item => Object.keys(item)[0]);
  const values = earningsPercentages.map(item => Object.values(item)[0]);

  const barData = {
    labels: months, // Labels for months
    datasets: [
      {
        label: 'Earning Percentages',
        data: values,
        backgroundColor: theme === "dark" ? '#7F5FC1' : '#4A90E2',
        borderColor: theme === "dark" ? '#7F5FC1' : '#4A90E2',
        borderWidth: 1,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        labels: {
          color: theme === "dark" ? '#fff' : '#000', // Change legend text color
        },
      },
      tooltip: {
        titleColor: theme === "dark" ? '#fff' : '#000', // Tooltip title color
        bodyColor: theme === "dark" ? '#fff' : '#000',  // Tooltip body color
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme === "dark" ? '#fff' : '#000', // X-axis labels (months) color
        },
        grid: {
          color: theme === "dark" ? '#444' : '#ddd', // Grid line color
        },
      },
      y: {
        ticks: {
          color: theme === "dark" ? '#fff' : '#000', // Y-axis labels color
        },
        grid: {
          color: theme === "dark" ? '#444' : '#ddd', // Grid line color
        },
      },
    },
  };

  const pieData = {
    labels: ['Taken', 'Returned', 'Replaced'],
    datasets: [
      {
        data: [sparesData.Taken, sparesData.Returned, sparesData.Replaced],
        backgroundColor: theme === "dark" ? ['#4A90E2', '#F1C40F', '#E74C3C'] : ['#2980B9', '#F39C12', '#E74C3C'],
        hoverOffset: 4,
      },
    ],
    options: {
      responsive: true,
      plugins: {
        tooltip: {
          callbacks: {
            label: function (tooltipItem) {
              return `${tooltipItem.label}: ${tooltipItem.raw}`;
            },
          },
          titleColor: theme === "dark" ? '#fff' : '#000',
          bodyColor: theme === "dark" ? '#fff' : '#000',
        },
      },
    },
  };


  return (
    <div className={`dashboard-container min-h-screen ${theme === "dark" ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-900"}`}>
      <div className="md:ml-[250px] w-full md:w-[calc(100%-250px)] mt-16 px-4 py-2 box-border overflow-auto">

        {loading && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50 z-50">
            <img src={engineGif} alt="Loading..." className="w-24 h-24" />
          </div>
        )}



        <h1 className={`text-xl font-bold mb-5 ms-2 ${theme === "dark" ? "text-white" : "text-gray-500"}`}>Dashboard</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
          {cards.map((card) => (
            <div
              key={card.id}
              onClick={() => toggleCard(card.id)}
              className={`relative p-4 cursor-pointer rounded-xl shadow-lg transition-all duration-300 ${expandedCard === card.id
                ? "col-span-2 bg-gradient-to-r from-blue-500 to-purple-600 scale-105 text-white backdrop-blur-lg"
                : theme === "dark" ? "bg-gray-700 text-white hover:shadow-2xl" : "bg-white hover:shadow-2xl"
                }`}
              style={{
                backdropFilter: expandedCard === card.id ? "blur(10px)" : "none",
              }}
            >
              <div className={`absolute top-4 right-4 ${theme === "dark" ? "text-gray-200" : "text-gray-600"} opacity-70`}>
                {React.cloneElement(card.icon, { color: theme === "dark" ? "white" : "black" })}
              </div>
              <h3 className={`text-lg font-bold ${expandedCard === card.id ? "text-white" : theme === "dark" ? "text-white" : "text-gray-600"}`}>
                {card.title}
              </h3>
              <p className={`text-2xl mt-2 font-semibold ${expandedCard === card.id ? "text-white" : theme === "dark" ? "text-white" : "text-gray-800"}`}>
                {card.value}
              </p>
              {expandedCard === card.id && (
                <p className="mt-4 text-sm">{card.detail}</p>
              )}

              <div className="w-full h-2 bg-gray-300 rounded-full mt-4">
                <div
                  className="h-2 rounded-full"
                  style={{
                    width: `${card.progress}%`,
                    backgroundColor: expandedCard === card.id ? "white" : "#7F5FC1",
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <div className={`shadow-md rounded-lg p-6 ${theme === "dark" ? "bg-gray-700 text-white" : "bg-white text-gray-900"}`}>
            <h3 className={`mb-4 ${theme === "dark" ? "text-white" : "text-gray-600"}`}>Earning Percentages (Bar Chart)</h3>
            <Bar data={barData} options={{ responsive: true }} />
          </div>

          <div className={`shadow-md rounded-lg p-6 ${theme === "dark" ? "bg-gray-700 text-white" : "bg-white text-gray-900"}`}>
            <h3 className={`mb-4 ${theme === "dark" ? "text-white" : "text-gray-600"}`}>Spares (Pie Chart)</h3>
            <Pie data={pieData} options={{ responsive: true }} />
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
